import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { addMonths, getDate, isPast, isSameDay, isSameMinute, isSameMonth } from 'date-fns';

import styles from './Calendar.module.scss';

import { getDaysOfWeekTranslated, getMonthTranslated } from './TimeRelated.enum';
import { convertToLocaleAndFormat } from '../../../utils/time-helpers';
import { setCurrentDate, setSelectedDate, setSelectedTimestamp } from '../../../actions/bookable';
import LoadingSpinner from '../loading/spinner/LoadingSpinner';
import LoadingWithMessages from '../loading/loading-with-messages/LoadingWithMessages';
import Button from '../button/Button';
import mixpanelHelper from '../../../utils/mixpanel-helper';
import { isAlternativeOnlyDate, isMixedAvailabilityDate, getAlternativeOnlyTimes } from '../../../utils/date-helpers';

const bookingLoadingMessages = [
  'Checking your coach’s availability…',
  'Reserving your session time slot…',
  'Syncing with your coach’s schedule…',
  'Finalizing your session details…',
  'Preparing your upcoming session…'
];

const Calendar = ({
  actionButtonText,
  alreadyBooked,
  calendar,
  currentLang,
  daysAvailableThisMonth,
  disabled,
  disabledFinishButton,
  futureSchedule,
  isFinishing,
  isLoading,
  onFinish,
  onMonthChangeForward,
  onDirectTimeSelection,
  renderAlert,
  renderAppointmentItem,
  renderAdditionalContent,
  selectedTimestamp,
  selectedTimestamps,
  setCurrentDateAction,
  setSelectedDateAction,
  setSelectedTimestampAction,
  timesAvailableThisDay,
  timezone,
  alternativeAvailabilities,
  onAlternativeTimeSelection,
  onMonthChange
}) => {
  const sumMonthAndYear = (calendar) => Number(calendar?.month || 0) + Number(calendar?.year || 0);
  const monthPlusYearRef = useRef(sumMonthAndYear(calendar));
  const bookingSummaryRef = useRef(null);

  useEffect(() => {
    if (selectedTimestamp) setCurrentDateAction(selectedTimestamp);
  }, [selectedTimestamp]);

  // Scroll to top and lock scrolling when finishing/loading
  useEffect(() => {
    if (isFinishing && bookingSummaryRef.current) {
      // Scroll to top
      bookingSummaryRef.current.scrollTop = 0;
      // Lock scrolling
      bookingSummaryRef.current.style.overflow = 'hidden';
    } else if (!isFinishing && bookingSummaryRef.current) {
      // Restore scrolling
      bookingSummaryRef.current.style.overflow = 'auto';
    }
  }, [isFinishing]);

  const updateRef = (calendar) => (monthPlusYearRef.current = sumMonthAndYear(calendar));

  useEffect(() => {
    if (sumMonthAndYear(calendar) > monthPlusYearRef.current) {
      if (onMonthChangeForward) onMonthChangeForward(calendar?.date);
    }
    updateRef(calendar);
  }, [calendar?.month]);

  const isDayAvailable = (day) => {
    if (isPast(new Date(day))) return false;
    const hasPrimaryAvailability = daysAvailableThisMonth.some((ts) => isSameDay(new Date(day), new Date(ts)));
    // Only show alternative availability if primary coach has availability for this month
    const hasAlternativeAvailability =
      primaryCoachHasMonthlyAvailability(day) && isAlternativeOnlyDate(day, daysAvailableThisMonth, alternativeAvailabilities);
    return hasPrimaryAvailability || hasAlternativeAvailability;
  };

  // Check if primary coach has availability this month AND user hasn't maxed out sessions
  const primaryCoachHasMonthlyAvailability = (day) => {
    const dayMonth = new Date(day).getMonth();
    const dayYear = new Date(day).getFullYear();

    // Check if there are primary coach times available for this month
    const hasPrimaryTimesThisMonth = daysAvailableThisMonth.some((ts) => {
      const tsDate = new Date(ts);
      return tsDate.getMonth() === dayMonth && tsDate.getFullYear() === dayYear;
    });

    // If no primary times at all for this month, it means credits are maxed out
    return hasPrimaryTimesThisMonth;
  };

  const handleDaySelection = (newDate) => {
    console.log('📅 CALENDAR DEBUG - handleDaySelection called with:', newDate, 'disabled:', disabled);
    if (disabled) {
      console.log('📅 CALENDAR DEBUG - Calendar is disabled, ignoring day selection');
      return;
    }
    if (!newDate || !isDayAvailable(newDate)) return;
    setSelectedDateAction(newDate);
    mixpanelHelper.track('Date Selected', {
      selected_timestamp: newDate
    });
  };

  const handleTimestampSelection = (timestamp, isAlternativeOrCoach = false) => {
    console.log(
      '🕐 CALENDAR DEBUG - handleTimestampSelection called with:',
      timestamp,
      'disabled:',
      disabled,
      'isAlternativeOrCoach:',
      isAlternativeOrCoach
    );

    console.log('🕐 CALENDAR DEBUG - Props check:', {
      onAlternativeTimeSelection: !!onAlternativeTimeSelection,
      onDirectTimeSelection: !!onDirectTimeSelection,
      alternativeAvailabilities: alternativeAvailabilities
    });

    if (disabled) {
      console.log('🕐 CALENDAR DEBUG - Calendar is disabled, ignoring timestamp selection');
      return;
    }
    // For alternative coach selections, we need different validation
    // since the timestamp might not be in primary availability
    if (!timestamp) {
      console.log('🕐 CALENDAR DEBUG - No timestamp provided');
      return;
    }

    // isAlternativeOrCoach can be either boolean true or the coach object
    const isAlternative = !!isAlternativeOrCoach;
    const coach = typeof isAlternativeOrCoach === 'object' ? isAlternativeOrCoach : null;

    // For alternative coach times, check if the timestamp exists in alternativeAvailabilities
    // For primary coach times, use the original isDayAvailable check
    if (!isAlternative && !isDayAvailable(timestamp)) {
      console.log('🕐 CALENDAR DEBUG - Primary time not available:', timestamp, isDayAvailable(timestamp));
      return;
    }

    if (isAlternative && alternativeAvailabilities && !alternativeAvailabilities[timestamp]) {
      console.log('🕐 CALENDAR DEBUG - Alternative time not found in availabilities:', timestamp);
      return;
    }

    console.log('🕐 CALENDAR DEBUG - Validation passed - Parsed values:', {
      isAlternative,
      coach,
      hasCoach: !!coach,
      coachType: typeof isAlternativeOrCoach
    });

    mixpanelHelper.track('Time Selected', {
      selected_timestamp: timestamp,
      is_alternative_coach: isAlternative
    });

    if (isAlternative && onAlternativeTimeSelection && coach) {
      console.log('🟠 CALENDAR DEBUG - Calling onAlternativeTimeSelection with:', timestamp, coach);
      onAlternativeTimeSelection(timestamp, coach);
    } else if (onDirectTimeSelection) {
      console.log('🟢 CALENDAR DEBUG - Calling onDirectTimeSelection with:', timestamp);
      onDirectTimeSelection(timestamp);
    } else {
      console.log('🔵 CALENDAR DEBUG - Calling setSelectedTimestampAction with:', timestamp);
      setSelectedTimestampAction(timestamp);
    }
  };

  const handleMonthChange = (value) => {
    const newDate = addMonths(new Date(calendar?.date), value);
    setCurrentDateAction(newDate);

    // Trigger fetching alternative availability for the new month
    if (onMonthChange) {
      onMonthChange(newDate);
    }
  };

  const isDayAlreadySelected = (day) => {
    return (selectedTimestamps || []).some((date) => isSameDay(new Date(date), new Date(day)));
  };

  const isDayPreviouslySelected = (day) => {
    return (alreadyBooked || []).some((date) => isSameDay(new Date(date), new Date(day)));
  };

  const isCurrentMonth = isSameMonth(new Date(), new Date(calendar?.date));

  console.log('futureSchedule', futureSchedule);

  return (
    <div className={styles.calendarAndClockWrapper}>
      <div className={styles.calendarAndTime}>
        <div className={[styles.calendar]}>
          <div className={styles.calendarHeader}>
            <button type="button" className={styles.arrow} onClick={handleMonthChange.bind(this, -1)} disabled={isCurrentMonth}>
              <div className={[styles.leftArrow, isCurrentMonth && styles.disabled].join(' ')} />
            </button>
            <div className={styles.monthAndYear}>
              <span id="dont-translate">{getMonthTranslated(currentLang)[calendar?.month]} </span>
              <span>{calendar?.year}</span>
            </div>
            <button type="button" className={styles.arrow} onClick={handleMonthChange.bind(this, 1)}>
              <div className={styles.rightArrow} />
            </button>
          </div>
          <div className={styles.calendarDays}>
            {calendar?.matrix?.map((arrayOfDays, index) => (
              <div className={styles.calendarColumn} key={index}>
                <div className={styles.weekDay} id="dont-translate">
                  {getDaysOfWeekTranslated(currentLang)[index].slice(0, 3).toUpperCase()}
                </div>
                {arrayOfDays.map((day, index) =>
                  day && getDate(new Date(day)) ? (
                    <button
                      className={[
                        styles.day,
                        isSameDay(new Date(day), new Date(selectedTimestamp)) && !disabled && styles.selected,
                        isDayAlreadySelected(day) && styles['already-selected'],
                        isDayPreviouslySelected(day) && styles['previously-selected'],
                        primaryCoachHasMonthlyAvailability(day) &&
                          isAlternativeOnlyDate(day, daysAvailableThisMonth, alternativeAvailabilities) &&
                          styles['alternative-only'],
                        primaryCoachHasMonthlyAvailability(day) &&
                          isMixedAvailabilityDate(day, daysAvailableThisMonth, alternativeAvailabilities) &&
                          styles['mixed-availability'],
                        (!isDayAvailable(day) || disabled) && styles.inactive
                      ].join(' ')}
                      type="button"
                      data-testid={isDayAvailable(day) && 'available-day'}
                      onClick={disabled ? undefined : handleDaySelection.bind(this, day)}
                      key={index}>
                      {getDate(new Date(day))}
                      {primaryCoachHasMonthlyAvailability(day) && isMixedAvailabilityDate(day, daysAvailableThisMonth, alternativeAvailabilities) && (
                        <span className={styles['orange-dot']} />
                      )}
                    </button>
                  ) : (
                    <div className={styles.emptySpace} key={index}>
                      .
                    </div>
                  )
                )}
              </div>
            ))}
            {isLoading && (
              <div className={styles.loaderWrapper}>
                <div className={styles.loader} />
              </div>
            )}
          </div>
          {Boolean(renderAlert) && !isLoading && <div className={styles.alert}>{renderAlert()}</div>}
        </div>
        {!isLoading &&
          (Boolean(timesAvailableThisDay?.length) ||
            getAlternativeOnlyTimes(selectedTimestamp, daysAvailableThisMonth, alternativeAvailabilities).length > 0) && (
            <div className={styles.timer}>
              <div className={styles.timerHeader}>
                <span>Time</span>
              </div>
              <div className={styles.availableTimes}>
                {(() => {
                  // Create primary coach time objects
                  const primaryTimes = timesAvailableThisDay.map((dateAndTime) => ({
                    timestamp: dateAndTime,
                    coach: null,
                    isPrimary: true
                  }));

                  // Get alternative times (only if primary coach has monthly availability)
                  const alternativeTimes = primaryCoachHasMonthlyAvailability(selectedTimestamp)
                    ? getAlternativeOnlyTimes(selectedTimestamp, daysAvailableThisMonth, alternativeAvailabilities).map((altTime) => ({
                        timestamp: altTime.timestamp,
                        coach: altTime.coach,
                        isPrimary: false
                      }))
                    : [];

                  // Combine and sort all times by time of day (ignoring the date part)
                  const allTimes = [...primaryTimes, ...alternativeTimes].sort((a, b) => {
                    // Parse timestamps and ensure proper date comparison
                    const dateA = typeof a.timestamp === 'string' ? new Date(a.timestamp) : a.timestamp;
                    const dateB = typeof b.timestamp === 'string' ? new Date(b.timestamp) : b.timestamp;

                    // Compare only by time of day (hours, minutes, seconds)
                    // This ensures AM times come before PM times regardless of the actual date
                    const timeA = dateA.getHours() * 3600 + dateA.getMinutes() * 60 + dateA.getSeconds();
                    const timeB = dateB.getHours() * 3600 + dateB.getMinutes() * 60 + dateB.getSeconds();

                    return timeA - timeB;
                  });

                  console.log('🔵 CALENDAR RENDER - Primary times:', timesAvailableThisDay);
                  console.log('🟠 CALENDAR RENDER - All times sorted:', allTimes);

                  return allTimes.map((timeObj, index) => {
                    if (timeObj.isPrimary) {
                      return (
                        <button
                          className={[
                            styles.timeSlots,
                            !onDirectTimeSelection && isSameMinute(new Date(timeObj.timestamp), new Date(selectedTimestamp)) && styles.selected,
                            disabled && styles.inactive
                          ].join(' ')}
                          type="button"
                          id="dont-translate"
                          onClick={disabled ? undefined : handleTimestampSelection.bind(this, timeObj.timestamp, false)}
                          key={`primary-${index}`}>
                          {convertToLocaleAndFormat(timeObj.timestamp, timezone, 'hh:mm aa')}
                        </button>
                      );
                    } else {
                      const coachName = timeObj.coach ? `${timeObj.coach.first_name} ${timeObj.coach.last_name}` : 'Alternative coach';
                      return (
                        <div key={`alternative-${index}`} className={styles.alternativeTimeWrapper}>
                          <button
                            className={[
                              styles.timeSlots,
                              styles['alternative-time'],
                              !onDirectTimeSelection && isSameMinute(new Date(timeObj.timestamp), new Date(selectedTimestamp)) && styles.selected,
                              disabled && styles.inactive
                            ].join(' ')}
                            type="button"
                            id="dont-translate"
                            onClick={disabled ? undefined : handleTimestampSelection.bind(this, timeObj.timestamp, timeObj.coach)}>
                            {convertToLocaleAndFormat(timeObj.timestamp, timezone, 'hh:mm aa')}
                            <p className={styles.alternativeTimeWrapperSubtitle}>{`With ${coachName}`}</p>
                          </button>
                        </div>
                      );
                    }
                  });
                })()}
              </div>
            </div>
          )}
      </div>
      <div className={styles.bookingSummary} ref={bookingSummaryRef}>
        <h1>Booking Summary</h1>
        {renderAdditionalContent && renderAdditionalContent()}
        <div className={styles.bookingSummaryList}>
          {isLoading ? (
            <div className={styles.loadingContainer}>
              <LoadingSpinner />
            </div>
          ) : (
            <ul>{futureSchedule.map((lesson, index) => renderAppointmentItem(lesson, index))}</ul>
          )}
        </div>
        <div className={styles.buttonsWrapper}>
          <Button
            outfit="greenButton"
            disabled={isLoading || disabledFinishButton || !futureSchedule.length}
            loading={false}
            onClick={onFinish}
            id="rescheduling-modal-book">
            {actionButtonText || 'Book'}
          </Button>
        </div>
        {isFinishing && (
          <div className={styles.bookingLoadingOverlay}>
            <LoadingWithMessages messages={bookingLoadingMessages} />
          </div>
        )}
      </div>
    </div>
  );
};

Calendar.propTypes = {
  actionButtonText: PropTypes.string,
  alreadyBooked: PropTypes.array,
  calendar: PropTypes.object.isRequired,
  currentLang: PropTypes.string,
  daysAvailableThisMonth: PropTypes.array.isRequired,
  disabled: PropTypes.bool,
  disabledFinishButton: PropTypes.bool,
  futureSchedule: PropTypes.array,
  isFinishing: PropTypes.bool,
  isLoading: PropTypes.bool,
  onFinish: PropTypes.func.isRequired,
  onMonthChangeForward: PropTypes.func.isRequired,
  onDirectTimeSelection: PropTypes.func,
  renderAlert: PropTypes.func,
  renderAppointmentItem: PropTypes.func.isRequired,
  renderAdditionalContent: PropTypes.func,
  selectedTimestamp: PropTypes.string.isRequired,
  selectedTimestamps: PropTypes.array,
  setCurrentDateAction: PropTypes.func.isRequired,
  setSelectedDateAction: PropTypes.func.isRequired,
  setSelectedTimestampAction: PropTypes.func.isRequired,
  shouldMoveToNextMonthWhenUnavailable: PropTypes.bool.isRequired,
  timesAvailableThisDay: PropTypes.array.isRequired,
  timezone: PropTypes.string.isRequired,
  alternativeAvailabilities: PropTypes.object,
  onAlternativeTimeSelection: PropTypes.func,
  onMonthChange: PropTypes.func
};

Calendar.defaultProps = {
  disabled: false,
  isLoading: false,
  selectedTimestamps: [],
  shouldMoveToNextMonthWhenUnavailable: false,
  alternativeAvailabilities: {},
  currentLang: 'en',
  onAlternativeTimeSelection: null,
  onMonthChange: null
};

const mapStateToProps = (state) => ({
  calendar: state.bookable.available.calendar,
  timezone: state.time.timezone
});

export default connect(mapStateToProps, {
  setCurrentDateAction: setCurrentDate,
  setSelectedDateAction: setSelectedDate,
  setSelectedTimestampAction: setSelectedTimestamp
})(Calendar);
